import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, RotateCcw, ShoppingBag, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, inr } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Stars } from "@/components/ProductCard";
import Particles, { GradientMesh } from "@/components/Particles";

const STEPS = [
  {
    key: "concern",
    title: "What is your skin asking for?",
    options: [
      { value: "acne", label: "Acne & Blemishes", desc: "Congestion, breakouts, marks" },
      { value: "uneven-tone", label: "Uneven Tone", desc: "Dark spots, pigmentation" },
      { value: "aging", label: "Fine Lines & Aging", desc: "Firmness, wrinkles, renewal" },
      { value: "dryness", label: "Dryness", desc: "Tightness, flaking, thirst" },
      { value: "glow", label: "Dullness & Glow", desc: "Tired skin, lost radiance" },
      { value: "hair-care", label: "Hair & Scalp", desc: "Hair fall, shine, scalp care" },
    ],
  },
  {
    key: "skin_type",
    title: "How does your skin behave by noon?",
    options: [
      { value: "oily", label: "Oily", desc: "Shine across the T-zone and beyond" },
      { value: "dry", label: "Dry", desc: "Tight, sometimes flaky" },
      { value: "combination", label: "Combination", desc: "Oily T-zone, normal cheeks" },
      { value: "normal", label: "Normal", desc: "Balanced and comfortable" },
      { value: "sensitive", label: "Sensitive", desc: "Reacts easily, redness-prone" },
    ],
  },
  {
    key: "climate",
    title: "Where does your skin live?",
    options: [
      { value: "humid-metro", label: "Humid Metro", desc: "Mumbai, Chennai, Kolkata" },
      { value: "dry-inland", label: "Dry Inland", desc: "Delhi, Jaipur, Nagpur" },
      { value: "cold-mountain", label: "Cold Mountain", desc: "Shimla, Manali, Srinagar" },
      { value: "polluted-urban", label: "Polluted Urban", desc: "High AQI city living" },
    ],
  },
  {
    key: "ritual",
    title: "How much ritual can you commit to?",
    options: [
      { value: "minimal", label: "Minimalist", desc: "2 steps, under 2 minutes" },
      { value: "complete", label: "Complete Ritual", desc: "5 steps + weekly treatments" },
    ],
  },
];

export default function Quiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const { addToCart, setCoupon, setDrawerOpen } = useStore();
  const navigate = useNavigate();

  const current = STEPS[step];
  const progress = result ? 100 : (step / STEPS.length) * 100;

  const choose = (value) => {
    const next = { ...answers, [current.key]: value };
    setAnswers(next);
    if (step < STEPS.length - 1) {
      setTimeout(() => setStep(step + 1), 280);
    } else {
      setLoading(true);
      api.postQuiz(next)
        .then((r) => setResult(r))
        .catch(() => toast.error("The oracle is resting. Try again."))
        .finally(() => setLoading(false));
    }
  };

  const addRoutine = async () => {
    result.routine.forEach((r) => addToCart(r.product, r.product.variants[0].label));
    const total = result.routine.reduce((s, r) => s + r.product.variants[0].price, 0);
    try {
      const c = await api.validateCoupon({ code: "RITUAL15", subtotal: total });
      if (c.valid) setCoupon(c);
      toast.success("Full ritual added — RITUAL15 (15% off) applied at checkout");
    } catch {
      toast.success("Full ritual added to your bag");
    }
    setDrawerOpen(true);
  };

  return (
    <div className="relative flex min-h-screen items-center overflow-hidden px-4 pb-20 pt-28 sm:px-6" data-testid="quiz-page">
      <GradientMesh />
      <Particles count={14} />
      <div className="relative mx-auto w-full max-w-3xl">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <p className="eyebrow">AI Skin Quiz</p>
            <p className="font-mono text-[0.65rem]" style={{ color: "var(--mn-text2)" }} data-testid="quiz-progress-text">
              {result ? "Complete" : `Step ${step + 1} of ${STEPS.length}`}
            </p>
          </div>
          <div className="mt-3 h-1 overflow-hidden rounded-full" style={{ background: "var(--mn-surface)" }}>
            <motion.div className="h-full rounded-full" style={{ background: "linear-gradient(90deg, var(--mn-gold), var(--mn-mint))" }} animate={{ width: `${progress}%` }} transition={{ duration: 0.5 }} />
          </div>
        </div>

        <AnimatePresence mode="wait">
          {!result && !loading && (
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              <h1 className="font-serif text-3xl tracking-tight sm:text-4xl" data-testid="quiz-question">{current.title}</h1>
              <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {current.options.map((o) => (
                  <button
                    key={o.value}
                    data-testid={`quiz-option-${o.value}`}
                    onClick={() => choose(o.value)}
                    className="glass-card group flex items-center justify-between rounded-2xl p-5 text-left transition-all duration-300 hover:-translate-y-0.5"
                    style={{ borderColor: answers[current.key] === o.value ? "var(--mn-gold)" : undefined }}
                  >
                    <span>
                      <span className="block font-serif text-lg transition-colors group-hover:text-[var(--mn-gold)]">{o.label}</span>
                      <span className="mt-0.5 block text-xs" style={{ color: "var(--mn-text2)" }}>{o.desc}</span>
                    </span>
                    <span className="ml-3 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-all group-hover:border-[var(--mn-gold)]" style={{ borderColor: "var(--mn-glass-border)" }}>
                      {answers[current.key] === o.value && <Check size={13} style={{ color: "var(--mn-gold)" }} />}
                    </span>
                  </button>
                ))}
              </div>
              {step > 0 && (
                <button data-testid="quiz-back" onClick={() => setStep(step - 1)} className="mt-6 flex items-center gap-2 text-sm transition-colors hover:text-[var(--mn-gold)]" style={{ color: "var(--mn-text2)" }}>
                  <ArrowLeft size={15} /> Back
                </button>
              )}
            </motion.div>
          )}

          {loading && (
            <motion.div key="loading" className="flex flex-col items-center py-20 text-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="quiz-analyzing">
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
                <Sparkles size={40} style={{ color: "var(--mn-gold)" }} />
              </motion.div>
              <p className="mt-6 font-serif text-2xl">Reading your skin's story…</p>
              <p className="mt-2 text-sm" style={{ color: "var(--mn-text2)" }}>Cross-referencing botanicals with actives.</p>
            </motion.div>
          )}

          {result && (
            <motion.div key="result" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid="quiz-result">
              <div className="glass-card rounded-[2rem] p-8 sm:p-10">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="eyebrow">Your Bespoke Ritual</p>
                    <h2 className="mt-2 font-serif text-3xl">Decoded for your skin</h2>
                  </div>
                  <div className="glass flex h-20 w-20 flex-col items-center justify-center rounded-full glow-mint">
                    <span className="font-serif text-2xl" style={{ color: "var(--mn-mint)" }} data-testid="quiz-match">{result.match}%</span>
                    <span className="font-mono text-[0.5rem] uppercase tracking-widest" style={{ color: "var(--mn-text2)" }}>match</span>
                  </div>
                </div>
                <p className="mt-4 text-sm leading-relaxed" style={{ color: "var(--mn-text2)" }} data-testid="quiz-summary">{result.summary}</p>

                <div className="mt-8 space-y-3">
                  {result.routine.map((r, i) => (
                    <motion.div
                      key={r.product.slug}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.15 + i * 0.1 }}
                      className="flex items-center gap-4 rounded-2xl border p-4"
                      style={{ borderColor: "var(--mn-glass-border)", background: "var(--mn-surface)" }}
                      data-testid={`quiz-routine-${r.product.slug}`}
                    >
                      <img src={r.product.images[0]} alt={r.product.name} className="h-16 w-14 rounded-xl object-cover" loading="lazy" />
                      <div className="min-w-0 flex-1">
                        <p className="font-mono text-[0.58rem] uppercase tracking-[0.25em]" style={{ color: "var(--mn-gold)" }}>
                          Step {i + 1} · {r.step} · {r.time}
                        </p>
                        <button onClick={() => navigate(`/product/${r.product.slug}`)} className="mt-0.5 truncate font-serif text-base hover:text-[var(--mn-gold)]">
                          {r.product.name}
                        </button>
                        <div className="mt-0.5 flex items-center gap-2">
                          <Stars value={r.product.rating} size={11} />
                          <span className="font-mono text-xs" style={{ color: "var(--mn-text2)" }}>{inr(r.product.variants[0].price)}</span>
                        </div>
                      </div>
                      <button
                        data-testid={`quiz-add-${r.product.slug}`}
                        onClick={() => { addToCart(r.product, r.product.variants[0].label); toast.success("Added to your bag"); }}
                        className="btn-outline-glow shrink-0 rounded-full p-2.5"
                        aria-label={`Add ${r.product.name}`}
                      >
                        <ShoppingBag size={15} />
                      </button>
                    </motion.div>
                  ))}
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                  <button data-testid="quiz-add-routine" onClick={addRoutine} className="btn-gold flex flex-1 items-center justify-center gap-2 rounded-full py-4 text-sm" style={{ minWidth: 220 }}>
                    <ShoppingBag size={16} /> Add Full Ritual — Save 15% <ArrowRight size={15} />
                  </button>
                  <button
                    data-testid="quiz-restart"
                    onClick={() => { setResult(null); setStep(0); setAnswers({}); }}
                    className="btn-outline-glow flex items-center gap-2 rounded-full px-6 py-4 text-sm"
                  >
                    <RotateCcw size={14} /> Retake
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
